import { useEffect, useState } from 'react';
import { useInsforge } from '../provider/InsforgeProvider';
import { VerifyEmailStatus } from './forms/VerifyEmailStatus';
import { VerifyEmailResponse } from '@insforge/shared-schemas';

export interface VerifyEmailProps {
  /** Verification token (from URL query params) */
  token: string;
  /** Text customization */
  verifyingTitle?: string;
  successTitle?: string;
  successMessage?: string;
  errorTitle?: string;
  /** Callback when verification is successful */
  onSuccess?: (data: VerifyEmailResponse) => void;
  /** Callback when verification fails */
  onError?: (error: Error) => void;
}

/**
 * Pre-built email verification component with full business logic.
 *
 * Automatically verifies the email when mounted using the provided token.
 *
 * @component
 * @example
 * ```tsx
 * const token = new URLSearchParams(window.location.search).get('token');
 *
 * <VerifyEmail
 *   token={token || ''}
 *   onSuccess={(data) => {
 *     console.log('Email verified!', data);
 *     // Optionally navigate or close window
 *   }}
 *   onError={(error) => console.error('Verification failed:', error)}
 * />
 * ```
 */
export function VerifyEmail({ token, onSuccess, onError, ...uiProps }: VerifyEmailProps) {
  const { verifyEmail } = useInsforge();
  const [status, setStatus] = useState<'verifying' | 'success' | 'error'>('verifying');
  const [error, setError] = useState('');

  useEffect(() => {
    const verifyEmailFn = async () => {
      if (!token) {
        const errorMessage = 'Invalid verification link. Missing required token.';
        setError(errorMessage);
        setStatus('error');
        if (onError) {
          onError(new Error(errorMessage));
        }
        return;
      }

      try {
        const result = await verifyEmail(token);

        if ('error' in result) {
          const errorMessage = result.error || 'Email verification failed';
          setError(errorMessage);
          setStatus('error');
          if (onError) {
            onError(new Error(errorMessage));
          }
          return;
        }

        setStatus('success');
        if (onSuccess) {
          onSuccess(result);
        }
      } catch (err: unknown) {
        const errorMessage = err instanceof Error ? err.message : 'Email verification failed';
        setError(errorMessage);
        setStatus('error');
        if (onError) {
          onError(new Error(errorMessage));
        }
      }
    };

    void verifyEmailFn();
  }, [token, verifyEmail, onSuccess, onError]);

  return <VerifyEmailStatus status={status} error={error} {...uiProps} />;
}
